import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  Popper,
  Typography,
  Switch
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useStyles } from './CourierDropdown.styles';
import { TextConstants } from '../../constants/TextConstants';

interface CourierDropdownProps {
  selectedCourier: string;
  courierList: Array<{ name: string; value: string }>;
  loading: boolean;
  onApply: (courierId: string) => void;
  onClear: () => void;
  isSelectionMode?: boolean;
  onEnableSelection?: () => void;
}

const CourierDropdown: React.FC<CourierDropdownProps> = ({
  selectedCourier,
  courierList,
  loading,
  onApply,
  onClear,
  isSelectionMode = false,
  onEnableSelection,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempSelected, setTempSelected] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (anchorEl) {
      setTempSelected(selectedCourier);
      setSearchText('');
    }
  }, [anchorEl, selectedCourier]);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isSelectionMode) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onApply(tempSelected);
    handleClose();
  };

  const handleClear = () => {
    onClear();
    handleClose();
  };

  const handleItemClick = (courierId: string) => {
    setTempSelected(courierId);
  };

  const handleSwitchToggle = () => {
    onEnableSelection?.();
  };

  const filteredCouriers = useMemo(
    () => courierList.filter((courier) =>
      courier.name.toLowerCase().includes(searchText.toLowerCase())
    ),
    [courierList, searchText]
  );

  const selectedCourierName = useMemo(
    () => courierList.find((c) => c.value === selectedCourier)?.name,
    [courierList, selectedCourier]
  );

  const open = Boolean(anchorEl);

  if (!isSelectionMode) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.labelText}>
          {TextConstants.ASSIGN_TO_COURIER}
        </Typography>
        <Switch
          checked={false}
          onChange={handleSwitchToggle}
          color="primary"
          inputProps={{ 'aria-label': TextConstants.ENABLE_COURIER_ASSIGNMENT }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.container}>
        <Box 
          className={classes.clickableBox}
          onClick={handleButtonClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleButtonClick(e as any);
            }
          }}
        >
          <Typography className={classes.primaryText}>
            {selectedCourierName || TextConstants.ASSIGN_TO_COURIERS}
          </Typography>
          <ArrowDropDownIcon className={classes.dropdownIcon} />
        </Box>
        <Switch
          checked={true}
          onChange={handleSwitchToggle}
          color="primary"
          size="small"
          inputProps={{ 'aria-label': TextConstants.DISABLE_COURIER_ASSIGNMENT }}
        />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className={classes.paper} elevation={3}>
            <Box className={classes.searchContainer}>
              <TextField
                placeholder={TextConstants.SEARCH_COURIER}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                autoFocus
                inputProps={{ 'aria-label': TextConstants.SEARCH_COURIERS }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className={classes.listContainer}>
              {loading ? (
                <Box className={classes.loadingContainer}>
                  <CircularProgress size={24} className={classes.loadingSpinner} />
                  <Typography variant="body2">{TextConstants.LOADING}</Typography>
                </Box>
              ) : filteredCouriers.length === 0 ? (
                <Typography variant="body2" className={classes.noResults}>
                  {TextConstants.NO_COURIERS_FOUND}
                </Typography>
              ) : (
                <List>
                  {filteredCouriers.map((courier) => (
                    <ListItem
                      key={courier.value}
                      button
                      selected={tempSelected === courier.value}
                      className={classes.listItem}
                      onClick={() => handleItemClick(courier.value)}
                    >
                      <ListItemText primary={courier.name} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleApply}
                disabled={!tempSelected}
                className={classes.applyButton}
              >
                {TextConstants.APPLY}
              </Button>

              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={handleClear}
                className={classes.clearButton}
              >
                {TextConstants.CANCEL}
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default CourierDropdown;
